import { useState } from 'react';
import ModalFlow from 'src/common/ModalFlow';
import { useClientsContext } from '../context/ClientsContext';
import FamilyDetailsModal from '../FamilyDetailsModal';
import { useCreateFamily } from '../hooks/useCreateFamily';
import { useUpdateClient } from '../hooks/useUpdateClient';
import AddContactsToFamilyModal from './AddContactsToFamily/AddContactsToFamilyModal';
import {
  InProgressFamily,
  membersOnDifferentAdvisor,
  starModelPayloadFromInProgressFamily,
} from './dataHelpers';
import DefineRelationshipsModal from './DefineRelationshipsModal';
import ConfirmReassignAdvisorModal from './ConfirmReassignAdvisorModal';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { useNavigate } from 'react-router';

export default function CreateFamilyFlow() {
  const { flow, close } = useClientsContext();
  const { createFamily } = useCreateFamily();
  const { updateClient, isLoading: isReassigning } = useUpdateClient();
  const navigate = useNavigate();

  const [flowKey, setFlowKey] = useState(0);
  const [initialData, setInitialData] = useState<InProgressFamily | undefined>(undefined);
  const [pendingFamily, setPendingFamily] = useState<InProgressFamily | null>(null);

  const opened = flow === 'create-family';
  if (!opened) {
    return null;
  }

  const submitFamily = async (family: InProgressFamily) => {
    try {
      await createFamily(starModelPayloadFromInProgressFamily(family));
      Mixpanel.track(MixpanelEvents.FAMILY_CREATED);
      close();
      navigate(0);
    } catch (error) {
      const message = ApiError.isApiError(ApiErrorCode.CLIENT_ALREADY_IN_FAMILY, error)
        ? error.message
        : 'An error occurred while creating the family. Please try again.';

      notifyAndLogError('Failed to create family', message, error, { familyData: family });
    }
  };

  const handleCreateFamily = async (family: InProgressFamily) => {
    if (!family) {
      throw new Error('No family selected');
    }

    if (membersOnDifferentAdvisor(family).length > 0) {
      setPendingFamily(family);
      return;
    }

    await submitFamily(family);
  };

  const handleConfirmReassign = async () => {
    if (!pendingFamily?.advisorUserId) {
      return;
    }

    const advisorUserId = pendingFamily.advisorUserId;
    try {
      await Promise.all(
        membersOnDifferentAdvisor(pendingFamily).map(member =>
          updateClient(member.id, { advisorUserId }),
        ),
      );
    } catch (error) {
      notifyAndLogError(
        'Failed to reassign clients',
        'An error occurred while reassigning clients. Please try again.',
        error,
        { familyData: pendingFamily },
      );
      return;
    }

    const family = pendingFamily;
    setPendingFamily(null);
    await submitFamily(family);
  };

  const handleCancelReassign = () => {
    setInitialData(pendingFamily ?? undefined);
    setFlowKey(key => key + 1);
    setPendingFamily(null);
  };

  return (
    <>
      <ModalFlow<InProgressFamily>
        key={flowKey}
        initialData={initialData}
        steps={[FamilyDetailsModal, AddContactsToFamilyModal, DefineRelationshipsModal]}
        onComplete={handleCreateFamily}
        onClose={close}
      />
      {pendingFamily && (
        <ConfirmReassignAdvisorModal
          conflictingMembers={membersOnDifferentAdvisor(pendingFamily)}
          targetAdvisorUserId={pendingFamily.advisorUserId}
          isLoading={isReassigning}
          onConfirm={handleConfirmReassign}
          onCancel={handleCancelReassign}
        />
      )}
    </>
  );
}
